import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/kit";
import { PROJECT_NAME } from "@/config/actra";

const LOOP = ["TASK", "AGENT", "ACTION", "EVIDENCE", "VERIFY", "PROOF", "REPUTATION", "USDC", "ARC"];

export const VerificationLoop = () => {
    const R = 42; // % radius
    const size = "min(88vw, 560px)";

    return (
        <section id="verification-loop" data-testid="verification-loop" className="section relative overflow-hidden">
            <div className="shell">
                <Reveal>
                    <SectionHeading
                        index="07"
                        eyebrow="SIGNATURE INTERACTION"
                        title={
                            <>
                                THE ACTION
                                <br />
                                VERIFICATION <span className="text-[#1677FF]">LOOP</span>
                            </>
                        }
                        description="ACTRA creates a complete economic cycle around autonomous actions — and the cycle compounds: every verified loop feeds the next task."
                        className="text-center [&>p]:mx-auto [&>p]:w-fit"
                    />
                </Reveal>

                <Reveal delay={0.15} className="mt-16">
                    <div className="relative mx-auto flex items-center justify-center" style={{ width: size, height: size }}>
                        {/* Orbit rings */}
                        <div className="absolute inset-0 rounded-full border border-white/[0.06]" aria-hidden="true" />
                        <div className="absolute inset-[10%] rounded-full border border-dashed border-white/[0.05]" aria-hidden="true" />

                        {/* Traveling pulse */}
                        <motion.div
                            className="absolute inset-0"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
                            aria-hidden="true"
                        >
                            <span
                                className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#31D8FF] shadow-[0_0_18px_#31D8FF]"
                            />
                        </motion.div>
                        <motion.div
                            className="absolute inset-[14%]"
                            animate={{ rotate: -360 }}
                            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                            aria-hidden="true"
                        >
                            <span className="absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#1677FF]" />
                        </motion.div>

                        {/* Center */}
                        <div className="absolute left-1/2 top-1/2 z-10 flex w-40 -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center">
                            <img
                                src="/actra-logo.png"
                                alt=""
                                className="h-16 w-16 object-contain"
                                style={{ mixBlendMode: "screen" }}
                            />
                            <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-mute">
                                {PROJECT_NAME} / 5042
                            </p>
                        </div>

                        {/* Nodes */}
                        {LOOP.map((node, i) => {
                            const angle = (i / LOOP.length) * 2 * Math.PI - Math.PI / 2;
                            const x = 50 + R * Math.cos(angle);
                            const y = 50 + R * Math.sin(angle);
                            return (
                                <motion.span
                                    key={node}
                                    data-testid={`loop-node-${node.toLowerCase()}`}
                                    className="absolute border border-white/10 bg-[#07111F]/90 px-2.5 py-1.5 font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-ink backdrop-blur-sm md:px-3.5 md:py-2 md:text-[10px]"
                                    style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                                    animate={{ opacity: [0.55, 1, 0.55], borderColor: ["rgba(255,255,255,0.12)", "rgba(22,119,255,0.6)", "rgba(255,255,255,0.12)"] }}
                                    transition={{ duration: 4.6, repeat: Infinity, delay: (i / LOOP.length) * 4.6 }}
                                >
                                    {node}
                                </motion.span>
                            );
                        })}
                    </div>
                    <p className="mx-auto mt-10 max-w-md text-center font-mono text-[10px] uppercase leading-relaxed tracking-[0.22em] text-mute">
                        THE LOOP RETURNS TO TASK — VERIFIED ECONOMIC ACTIVITY COMPOUNDS
                    </p>
                </Reveal>
            </div>
        </section>
    );
};

export default VerificationLoop;
